import type { Invoice, Subscription } from '../types/subscriptionManagement'
import { isPaidInvoiceStatus, parseYmdLocal } from './subscriptionInvoiceCoverage'
import { effectiveRenewalDate } from './subscriptionRenewal'

export type TimelineSegmentKind = 'coverage' | 'contract'

export interface TimelineSegment {
  subscriptionId: string
  subscriptionName: string
  kind: TimelineSegmentKind
  /** Inclusive period start */
  start: Date
  /** Exclusive period end (renewal / coverage end boundary) */
  endExclusive: Date
  detail?: string
}

export interface SubscriptionTimelineRow {
  subscriptionId: string
  name: string
  segments: TimelineSegment[]
}

export interface SubscriptionTimelineModel {
  rows: SubscriptionTimelineRow[]
  rangeStart: Date
  rangeEndExclusive: Date
}

const MS_DAY = 86400000

function fyWindow(fyStart: number): { start: Date; endExclusive: Date } {
  const start = parseYmdLocal(`${fyStart}-04-01`)!
  const endExclusive = parseYmdLocal(`${fyStart + 1}-04-01`)!
  return { start, endExclusive }
}

function segmentOverlapsRange(
  seg: TimelineSegment,
  rangeStart: Date,
  rangeEndExclusive: Date,
): boolean {
  return (
    seg.endExclusive.getTime() > rangeStart.getTime() &&
    seg.start.getTime() < rangeEndExclusive.getTime()
  )
}

function clipSegmentToRange(
  seg: TimelineSegment,
  rangeStart: Date,
  rangeEndExclusive: Date,
): TimelineSegment | null {
  if (!segmentOverlapsRange(seg, rangeStart, rangeEndExclusive)) return null
  const start = new Date(
    Math.max(seg.start.getTime(), rangeStart.getTime()),
  )
  const endExclusive = new Date(
    Math.min(seg.endExclusive.getTime(), rangeEndExclusive.getTime()),
  )
  if (endExclusive.getTime() <= start.getTime()) return null
  return { ...seg, start, endExclusive }
}

/**
 * Build Gantt rows: paid invoice coverage periods per subscription;
 * falls back to contract start → effective renewal when no coverage exists.
 */
export function buildSubscriptionTimeline(
  subscriptions: Subscription[],
  allInvoices: Invoice[],
  fyStart: number | 'all',
): SubscriptionTimelineModel | null {
  if (subscriptions.length === 0) return null

  const invBySub = new Map<string, Invoice[]>()
  for (const inv of allInvoices) {
    if (!inv.subscriptionId) continue
    const list = invBySub.get(inv.subscriptionId) || []
    list.push(inv)
    invBySub.set(inv.subscriptionId, list)
  }

  const rows: SubscriptionTimelineRow[] = []

  for (const sub of subscriptions) {
    const subInv = invBySub.get(sub.id) || []
    const segments: TimelineSegment[] = []

    for (const inv of subInv) {
      if (!isPaidInvoiceStatus(inv.paymentStatus)) continue
      const start = parseYmdLocal(inv.subscriptionStartDate)
      const endExclusive = parseYmdLocal(inv.subscriptionEndDate)
      if (!start || !endExclusive || endExclusive <= start) continue
      segments.push({
        subscriptionId: sub.id,
        subscriptionName: sub.name,
        kind: 'coverage',
        start,
        endExclusive,
        detail: inv.invoiceNumber
          ? `Invoice ${inv.invoiceNumber}`
          : undefined,
      })
    }

    if (segments.length === 0) {
      const start = parseYmdLocal(sub.startDate)
      const renewalYmd = effectiveRenewalDate(sub, allInvoices)
      const endExclusive = parseYmdLocal(renewalYmd)
      if (start && endExclusive && endExclusive > start) {
        segments.push({
          subscriptionId: sub.id,
          subscriptionName: sub.name,
          kind: 'contract',
          start,
          endExclusive,
          detail: 'Contract period',
        })
      }
    }

    if (segments.length === 0) continue

    segments.sort((a, b) => a.start.getTime() - b.start.getTime())
    rows.push({
      subscriptionId: sub.id,
      name: sub.name || sub.id,
      segments,
    })
  }

  if (rows.length === 0) return null

  rows.sort((a, b) => a.name.localeCompare(b.name))

  let rangeStart: Date
  let rangeEndExclusive: Date

  if (fyStart !== 'all') {
    const win = fyWindow(fyStart)
    rangeStart = win.start
    rangeEndExclusive = win.endExclusive
  } else {
    let minMs = Infinity
    let maxMs = -Infinity
    for (const row of rows) {
      for (const seg of row.segments) {
        minMs = Math.min(minMs, seg.start.getTime())
        maxMs = Math.max(maxMs, seg.endExclusive.getTime())
      }
    }
    rangeStart = new Date(minMs)
    rangeEndExclusive = new Date(maxMs)
    const pad = 7 * MS_DAY
    rangeStart = new Date(rangeStart.getTime() - pad)
    rangeEndExclusive = new Date(rangeEndExclusive.getTime() + pad)
  }

  const clippedRows: SubscriptionTimelineRow[] = rows
    .map(row => ({
      ...row,
      segments: row.segments
        .map(s => clipSegmentToRange(s, rangeStart, rangeEndExclusive))
        .filter((s): s is TimelineSegment => s != null),
    }))
    .filter(row => row.segments.length > 0)

  if (clippedRows.length === 0) return null

  return {
    rows: clippedRows,
    rangeStart,
    rangeEndExclusive,
  }
}

/** Month tick labels between rangeStart and rangeEndExclusive. */
export function timelineMonthTicks(
  rangeStart: Date,
  rangeEndExclusive: Date,
): { ms: number; label: string }[] {
  const ticks: { ms: number; label: string }[] = []
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  let y = rangeStart.getFullYear()
  let m = rangeStart.getMonth()
  const endMs = rangeEndExclusive.getTime()

  while (true) {
    const d = new Date(y, m, 1)
    if (d.getTime() >= endMs) break
    if (d.getTime() >= rangeStart.getTime()) {
      ticks.push({
        ms: d.getTime(),
        label: `${MONTHS[m]} '${String(y).slice(2)}`,
      })
    }
    m += 1
    if (m > 11) {
      m = 0
      y += 1
    }
    if (y > rangeEndExclusive.getFullYear() + 1) break
  }
  return ticks
}

export function msToPct(
  ms: number,
  rangeStart: Date,
  rangeEndExclusive: Date,
): number {
  const span = rangeEndExclusive.getTime() - rangeStart.getTime()
  if (span <= 0) return 0
  return ((ms - rangeStart.getTime()) / span) * 100
}

export function formatSegmentRange(seg: TimelineSegment): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  const lastInclusive = new Date(seg.endExclusive.getTime() - MS_DAY)
  return `${fmt(seg.start)} – ${fmt(lastInclusive)}`
}
